import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, CheckCircle, XCircle, ExternalLink, Link as LinkIcon } from 'lucide-react';
import api from '@/lib/api';
import { Firm } from '@/types/settings';

interface OAuthSettingsProps {
  firm: Firm;
  canModify: boolean;
  onUpdate: () => void;
}

export function OAuthSettings({ firm, canModify, onUpdate }: OAuthSettingsProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleInitiateOAuth = async () => {
    if (!canModify) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await api.post(`/api/firms/${firm.id}/ms-oauth-authorize/`);
      
      // Redirect to Microsoft OAuth
      if (response.data.authorization_url) {
        window.open(response.data.authorization_url, '_blank');
        setSuccess('OAuth authorization window opened. Please complete the authorization process.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initiate OAuth authorization');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshToken = async () => {
    if (!canModify) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await api.post(`/api/firms/${firm.id}/refresh-oauth-token/`);
      setSuccess('OAuth token refreshed successfully');
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to refresh OAuth token');
    } finally {
      setLoading(false);
    }
  };

  const getTokenExpiryDate = () => {
    if (firm.oauth_status.token_expires_at) {
      return new Date(firm.oauth_status.token_expires_at).toLocaleString();
    }
    return 'Unknown';
  };

  const isTokenExpiringSoon = () => {
    if (!firm.oauth_status.token_expires_at) return false;
    
    const expiryDate = new Date(firm.oauth_status.token_expires_at);
    const now = new Date();
    const hoursUntilExpiry = (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    return hoursUntilExpiry < 24; // Warning if expires within 24 hours
  };

  const handleConnectMicrosoftAccount = () => {
    // Redirect to the backend OAuth initiation endpoint
    const redirectUri = encodeURIComponent(window.location.href);
    window.location.href = `https://server.sentra.be/api/oauth/connect/?firm_id=${firm.id}&redirect_uri=${redirectUri}`;
  };

  return (
    <div className="space-y-6">
      {/* Microsoft Account Integration Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" />
            Microsoft Account Integration
          </CardTitle>
          <CardDescription>
            Connect your Microsoft account to send emails directly through the Sentra platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              By connecting your Microsoft account, you can send invoices, credit notes, and other documents 
              directly from Sentra using your organization's email account.
            </p>
            <Button 
              onClick={handleConnectMicrosoftAccount}
              disabled={!canModify || loading}
              className="gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Connect Microsoft Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* OAuth Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle>OAuth Configuration Details</CardTitle>
          <CardDescription>
            Advanced OAuth token management and status information
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!canModify && (
            <Alert>
              <AlertDescription>
                You don't have permission to modify OAuth settings.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="border-green-200 bg-green-50">
              <AlertDescription className="text-green-800">{success}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-6 mt-6">
            {/* OAuth Status Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-3">OAuth Status</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span>OAuth Enabled:</span>
                    <Badge variant={firm.oauth_status.oauth_enabled ? 'default' : 'secondary'}>
                      {firm.oauth_status.oauth_enabled ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Enabled</>
                      ) : (
                        <><XCircle className="w-3 h-3 mr-1" /> Disabled</>
                      )}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span>Access Token:</span>
                    <Badge variant={firm.oauth_status.has_access_token ? 'default' : 'destructive'}>
                      {firm.oauth_status.has_access_token ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Valid</>
                      ) : (
                        <><XCircle className="w-3 h-3 mr-1" /> Missing</>
                      )}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span>Refresh Token:</span>
                    <Badge variant={firm.oauth_status.has_refresh_token ? 'default' : 'destructive'}>
                      {firm.oauth_status.has_refresh_token ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Valid</>
                      ) : (
                        <><XCircle className="w-3 h-3 mr-1" /> Missing</>
                      )}
                    </Badge>
                  </div>

                  {firm.oauth_status.token_expires_at && (
                    <div className="flex items-center justify-between">
                      <span>Token Expires:</span>
                      <span className={`text-sm ${isTokenExpiringSoon() ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                        {getTokenExpiryDate()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Actions</h4>
                <div className="space-y-3">
                  {!firm.oauth_status.has_access_token && canModify && (
                    <Button 
                      onClick={handleInitiateOAuth} 
                      disabled={loading}
                      className="w-full"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {loading ? 'Initiating...' : 'Setup OAuth Authorization'}
                    </Button>
                  )}

                  {firm.oauth_status.has_refresh_token && canModify && (
                    <Button 
                      variant="outline"
                      onClick={handleRefreshToken} 
                      disabled={loading}
                      className="w-full"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {loading ? 'Refreshing...' : 'Refresh Token'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Warning for expiring token */}
            {isTokenExpiringSoon() && (
              <Alert variant="destructive">
                <AlertDescription>
                  <strong>Warning:</strong> Your OAuth token is expiring soon. Please refresh it to continue using email functionality.
                </AlertDescription>
              </Alert>
            )}

            {/* Setup Instructions */}
            {!firm.oauth_status.oauth_enabled && (
              <Alert>
                <AlertDescription>
                  <strong>Setup Required:</strong> OAuth is not enabled for your firm. Contact your administrator to configure Microsoft Graph API integration.
                </AlertDescription>
              </Alert>
            )}

            {/* Success state */}
            {firm.oauth_status.oauth_enabled && 
             firm.oauth_status.has_access_token && 
             firm.oauth_status.has_refresh_token && 
             !isTokenExpiringSoon() && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="w-4 h-4" />
                <AlertDescription className="text-green-800">
                  <strong>Success:</strong> OAuth is properly configured and tokens are valid.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
